import React, { useEffect, useState } from 'react';
import { Save, RotateCcw } from 'lucide-react';

const SETTING_INTENT_PROMPT = 'intent_classification_prompt';

const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:8000').replace(/\/$/, '');

function getAuthHeaders(): Record<string, string> {
  try {
    const raw = localStorage.getItem('auth-storage');
    const token = raw ? JSON.parse(raw)?.state?.token : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export const GeneralSettings: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${apiBase}/api/v1/settings`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const data: Record<string, string> = await res.json();
        const p = data[SETTING_INTENT_PROMPT] ?? '';
        setPrompt(p);
        setDefaultPrompt(p);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await fetch(`${apiBase}/api/v1/settings/${SETTING_INTENT_PROMPT}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ value: prompt }),
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      setDefaultPrompt(prompt);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPrompt(defaultPrompt);
    setSaved(false);
    setError(null);
  };

  const isDirty = prompt !== defaultPrompt;

  return (
    <div className="p-6 max-w-3xl">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">General Settings</h2>

      {/* Intent Classification Prompt */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-1">
          Intent Classification Prompt
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          This prompt instructs the DSPy model on how to match a user message to the best
          workflow template. Changes take effect immediately without restarting the server.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
            <div className="w-4 h-4 border-2 border-fuchsia-300 border-t-fuchsia-600 rounded-full animate-spin" />
            Loading…
          </div>
        ) : (
          <>
            <textarea
              value={prompt}
              onChange={e => { setPrompt(e.target.value); setSaved(false); }}
              rows={6}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono
                         focus:outline-none focus:ring-2 focus:ring-fuchsia-500 resize-y"
              placeholder="Enter the DSPy intent classification prompt…"
            />

            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}

            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={handleSave}
                disabled={saving || !isDirty}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
                           bg-fuchsia-600 text-white hover:bg-fuchsia-700
                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving…' : 'Save'}
              </button>

              {isDirty && (
                <button
                  onClick={handleReset}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
                             border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              )}

              {saved && (
                <span className="text-sm text-green-600 font-medium">
                  ✓ Saved — prompt updated
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
